import {autorun, observable, toJS, transaction} from 'mobx'
import {keys, merge, union, values} from 'lodash'
import {
  EpikConfig,
  PaymentCurrency,
  PaymentFlow,
  PaymentMethod,
  PaymentProvider,
  PaymentType,
  PseudoRecurringPaymentMethods,
  RecurringInterval,
  RecurringPaymentMethods,
} from '@raisenow/epik'
import {parse} from 'qs'
import {DynamicBlockProps} from '../components/dynamic/DynamicBlock'
import {DynamicBlockHeaderProps} from '../components/dynamic/DynamicBlockHeader'
import {DynamicFieldProps} from '../components/dynamic/DynamicField'
import {Block} from './Block'
import {ComputedField, ConfigCondition} from './Compiler'
import {PaymentFormData} from './forms/PaymentForm'
import {DynamicComponentType} from './DynamicComponents'
import {DynamicSlotName} from './DynamicSlotName'
import {findDefinedValue} from './helpers'
import {Translator, TranslatorMappedResource} from './Translator'
import {ValidationConstraints} from './Validator'
import {OverlayMode} from './OverlayMode'
import {PaymentSlipDeliveryType} from './PaymentSlipDeliveryType'

///////////////////////////////////////////////////////////////////////////////

export type AmountSubunitsConfig = {
  [key in PaymentCurrency]: number
}

export type PurposeDetailsConfig = {
  [key: string]: {
    stored_campaign_id: string
    stored_campaign_subid: string
  }
}

export type UiBreakpointsConfig = {
  [key: string]: number
}

export type StatesByCountry = {
  [key: string]: string[]
}

type SlotConstraint = {if?: string | boolean}
type CustomFieldSlotConfig = DynamicFieldProps &
  SlotConstraint & {component: DynamicComponentType.FIELD}
type BlockSlotConfig = DynamicBlockProps &
  SlotConstraint & {component: DynamicComponentType.BLOCK}
type BlockTitleSlotConfig = DynamicBlockHeaderProps &
  SlotConstraint & {component: DynamicComponentType.BLOCK_HEADER}
type BlockContentSlotConfig = DynamicBlockHeaderProps &
  SlotConstraint & {component: DynamicComponentType.BLOCK_CONTENT}

export type SlotComponentConfig =
  | CustomFieldSlotConfig
  | BlockSlotConfig
  | BlockTitleSlotConfig
  | BlockContentSlotConfig
export type SlotsConfig = {[key in DynamicSlotName]?: SlotComponentConfig[]}

export type WidgetConfig = {
  debug?: boolean
  debugErrorMessages?: boolean
  debugSlots?: boolean

  overlayMode?: OverlayMode
  language?: string
  returnParameters?: boolean
  useCreditCardIframe?: boolean
  testMode?: boolean
  flow?: PaymentFlow

  immediateExecution?: boolean | ConfigCondition<boolean>[]

  translations?: TranslatorMappedResource

  paymentWidgetBlocks?: ConfigCondition<Block>[]

  epikConfig?: EpikConfig
  paymentFormPrefill?: Partial<PaymentFormData>

  purposes?: ConfigCondition<string>[]
  paymentTypes?: ConfigCondition<PaymentType>[]
  recurringIntervals?: ConfigCondition<RecurringInterval>[]
  currencies?: ConfigCondition<PaymentCurrency>[]
  amounts?: ConfigCondition<number>[]

  coverFeeFixed?: ConfigCondition<number>[]
  coverFeePercentage?: ConfigCondition<number>[]

  paymentMethods?: ConfigCondition<PaymentMethod>[]
  priorityCreditCardTypes?: ConfigCondition<PaymentMethod>[]

  defaultPurpose?: string | ConfigCondition<string>[]
  defaultPaymentType?: PaymentType | ConfigCondition<PaymentType>[]
  defaultRecurringInterval?:
    | RecurringInterval
    | ConfigCondition<RecurringInterval>[]
  defaultCurrency?: PaymentCurrency | ConfigCondition<PaymentCurrency>[]
  defaultAmount?: number | ConfigCondition<number>[]
  defaultPaymentMethod?: PaymentMethod | ConfigCondition<PaymentMethod>[]

  autoselectPurpose?: boolean
  autoselectPaymentType?: boolean
  autoselectRecurringInterval?: boolean
  autoselectCurrency?: boolean
  autoselectAmount?: boolean
  autoselectPaymentMethod?: boolean

  purposeDetails?: PurposeDetailsConfig
  amountSubunits?: AmountSubunitsConfig
  allowCustomAmount?: boolean | ConfigCondition<boolean>[]
  minimumCustomAmount?: number | ConfigCondition<number>[]
  maximumCustomAmount?: number | ConfigCondition<number>[]
  paymentProvider?: PaymentProvider
  allowedRecurringPaymentMethods?: ConfigCondition<PaymentMethod>[]
  paymentSlipDeliveryTypes?: ConfigCondition<PaymentSlipDeliveryType>[]
  defaultPaymentSlipDeliveryType?:
    | PaymentSlipDeliveryType
    | ConfigCondition<PaymentSlipDeliveryType>[]

  faqEntries?: ConfigCondition<string>[]
  expandedFaqEntries?: ConfigCondition<string>[]

  salutations?: ConfigCondition<string>[]
  countries?: ConfigCondition<string>[]
  priorityCountries?: ConfigCondition<string>[]
  states?: StatesByCountry

  paymentComputations?: ConfigCondition<ComputedField>[]
  paymentValidations?: ValidationConstraints

  uiBreakpoints?: UiBreakpointsConfig
  uiTransitionTimeout?: number
  uiCustomCheckableElements?: boolean
  uiFloatingLabels?: boolean
  uiRippleEffects?: boolean
  uiCheckboxRadius?: number
  uiScrollOffset?: number
  uiScrollDuration?: number

  showStoredCustomerSalutation?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerFirstname?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerLastname?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerEmail?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerBirthdate?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerEmailPermission?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerMessage?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerDonationReceipt?: boolean | ConfigCondition<boolean>[]

  showStoredCustomerStreet?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerStreetNumber?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerStreet2?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerPobox?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerZipCode?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerCity?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerCountry?: boolean | ConfigCondition<boolean>[]
  showStoredCustomerState?: boolean | ConfigCondition<boolean>[]

  showBankbic?: boolean | ConfigCondition<boolean>[]

  showSubmitButton?: boolean | ConfigCondition<boolean>[]
  showFooter?: boolean | ConfigCondition<boolean>[]
  showTestModeBar?: boolean | ConfigCondition<boolean>[]
  showRetryPaymentButton?: boolean | ConfigCondition<boolean>[]

  ddFormTemplate?: string | ConfigCondition<string>[]
  oneClickPayment?: boolean | ConfigCondition<boolean>[]

  dateStoreFormat?: string
  dateDisplayFormat?: string

  customConfig?: any
  generatedConfig?: any

  recaptchaKey?: string

  slots?: SlotsConfig
}

///////////////////////////////////////////////////////////////////////////////

function createEmptyWidgetConfig(): WidgetConfig {
  return {
    debug: null,
    debugErrorMessages: null,
    debugSlots: null,

    overlayMode: null,
    language: null,
    returnParameters: null,
    useCreditCardIframe: null,
    testMode: null,
    flow: null,

    immediateExecution: null,

    translations: null,

    paymentWidgetBlocks: null,

    epikConfig: null,
    paymentFormPrefill: null,

    purposes: null,
    paymentTypes: null,
    recurringIntervals: null,
    currencies: null,
    amounts: null,

    coverFeeFixed: null,
    coverFeePercentage: null,

    paymentMethods: null,
    priorityCreditCardTypes: null,

    defaultPurpose: null,
    defaultPaymentType: null,
    defaultRecurringInterval: null,
    defaultCurrency: null,
    defaultAmount: null,
    defaultPaymentMethod: null,

    autoselectPurpose: null,
    autoselectPaymentType: null,
    autoselectRecurringInterval: null,
    autoselectCurrency: null,
    autoselectAmount: null,
    autoselectPaymentMethod: null,

    purposeDetails: null,
    amountSubunits: null,
    allowCustomAmount: null,
    minimumCustomAmount: null,
    maximumCustomAmount: null,
    paymentProvider: null,
    allowedRecurringPaymentMethods: null,
    paymentSlipDeliveryTypes: null,
    defaultPaymentSlipDeliveryType: null,

    faqEntries: null,
    expandedFaqEntries: null,

    salutations: null,
    countries: null,
    priorityCountries: null,
    states: null,

    paymentComputations: null,
    paymentValidations: null,

    uiBreakpoints: null,
    uiTransitionTimeout: null,
    uiCustomCheckableElements: null,
    uiFloatingLabels: null,
    uiRippleEffects: null,
    uiCheckboxRadius: null,
    uiScrollOffset: null,
    uiScrollDuration: null,

    showStoredCustomerSalutation: null,
    showStoredCustomerFirstname: null,
    showStoredCustomerLastname: null,
    showStoredCustomerEmail: null,
    showStoredCustomerBirthdate: null,
    showStoredCustomerEmailPermission: null,
    showStoredCustomerMessage: null,
    showStoredCustomerDonationReceipt: null,

    showStoredCustomerStreet: null,
    showStoredCustomerStreetNumber: null,
    showStoredCustomerStreet2: null,
    showStoredCustomerPobox: null,
    showStoredCustomerZipCode: null,
    showStoredCustomerCity: null,
    showStoredCustomerCountry: null,
    showStoredCustomerState: null,

    showBankbic: null,

    showSubmitButton: null,
    showFooter: null,
    showTestModeBar: null,
    showRetryPaymentButton: null,

    ddFormTemplate: null,
    oneClickPayment: null,

    dateStoreFormat: null,
    dateDisplayFormat: null,

    customConfig: null,
    generatedConfig: null,

    recaptchaKey: null,

    slots: null,
  }
}

function updateComputedConfig(
  config: WidgetConfig,
  initial: WidgetConfig,
  loaded: any,
  translations: TranslatorMappedResource,
) {
  transaction(() => {
    let forceDebug =
      window.location.hash === '#widgetdebug' ||
      keys(parse(window.location.search, {ignoreQueryPrefix: true})).indexOf(
        'widgetdebug',
      ) !== -1

    config.debug = findDefinedValue(initial.debug, loaded.debug, false)
    config.debug = forceDebug ? forceDebug : config.debug

    config.debugErrorMessages = findDefinedValue(
      initial.debugErrorMessages,
      loaded.debug_error_messages,
      false,
    )
    config.debugSlots = findDefinedValue(
      initial.debugSlots,
      loaded.debug_slots,
      false,
    )

    config.overlayMode = findDefinedValue(
      initial.overlayMode,
      loaded.overlay_mode,
      OverlayMode.OUTER,
    )
    config.language = initial.language
    config.returnParameters = findDefinedValue(
      initial.returnParameters,
      loaded.return_parameters,
      false,
    )
    config.useCreditCardIframe = findDefinedValue(
      initial.useCreditCardIframe,
      loaded.use_credit_card_iframe,
      false,
    )
    config.testMode = findDefinedValue(initial.testMode, loaded.test_mode, true)

    config.immediateExecution = findDefinedValue(
      initial.immediateExecution,
      loaded.immediate_execution,
      false,
    )

    config.flow = findDefinedValue(initial.flow, loaded.flow, PaymentFlow.EPP)

    config.translations = translations

    config.paymentWidgetBlocks = findDefinedValue(
      initial.paymentWidgetBlocks,
      loaded.payment_widget_blocks,
      [],
    )

    config.epikConfig = findDefinedValue(
      initial.epikConfig,
      loaded.epik_config,
      {},
    )
    config.paymentFormPrefill = findDefinedValue(
      initial.paymentFormPrefill,
      loaded.payment_form_prefill,
      {},
    )

    config.purposes = findDefinedValue(initial.purposes, loaded.purposes, [
      'p1',
      'p2',
      'p3',
      'p4',
    ])
    config.paymentTypes = findDefinedValue(
      initial.paymentTypes,
      loaded.payment_types,
      values(PaymentType) as PaymentType[],
    )
    config.recurringIntervals = findDefinedValue(
      initial.recurringIntervals,
      loaded.recurring_intervals,
      values(RecurringInterval) as RecurringInterval[],
    )
    config.currencies = findDefinedValue(
      initial.currencies,
      loaded.currencies,
      values(PaymentCurrency) as PaymentCurrency[],
    )
    config.amounts = findDefinedValue(initial.amounts, loaded.amounts, [
      5,
      10,
      25,
      100,
    ])

    config.coverFeeFixed = findDefinedValue(
      initial.coverFeeFixed,
      loaded.cover_fee_fixed,
      0,
    )
    config.coverFeePercentage = findDefinedValue(
      initial.coverFeePercentage,
      loaded.cover_fee_percentage,
      0,
    )

    config.paymentMethods = findDefinedValue(
      initial.paymentMethods,
      loaded.payment_methods,
      values(PaymentMethod) as PaymentMethod[],
    )
    config.priorityCreditCardTypes = findDefinedValue(
      initial.priorityCreditCardTypes,
      loaded.priority_credit_card_types,
      [
        PaymentMethod.VISA,
        PaymentMethod.MASTER_CARD,
        PaymentMethod.AMERICAN_EXPRESS,
      ],
    )

    config.defaultPurpose = findDefinedValue(
      initial.defaultPurpose,
      loaded.default_purpose,
    )
    config.defaultPaymentType = findDefinedValue(
      initial.defaultPaymentType,
      loaded.default_payment_type,
    )
    config.defaultRecurringInterval = findDefinedValue(
      initial.defaultRecurringInterval,
      loaded.default_recurring_interval,
    )
    config.defaultCurrency = findDefinedValue(
      initial.defaultCurrency,
      loaded.default_currency,
    )
    config.defaultAmount = findDefinedValue(
      initial.defaultAmount,
      loaded.default_amount,
    )
    config.defaultPaymentMethod = findDefinedValue(
      initial.defaultPaymentMethod,
      loaded.default_payment_method,
    )

    config.autoselectPurpose = findDefinedValue(
      initial.autoselectPurpose,
      loaded.autoselect_purpose,
      true,
    )
    config.autoselectPaymentType = findDefinedValue(
      initial.autoselectPaymentType,
      loaded.autoselect_payment_type,
      true,
    )
    config.autoselectRecurringInterval = findDefinedValue(
      initial.autoselectRecurringInterval,
      loaded.autoselect_recurring_interval,
      true,
    )
    config.autoselectCurrency = findDefinedValue(
      initial.autoselectCurrency,
      loaded.autoselect_currency,
      true,
    )
    config.autoselectAmount = findDefinedValue(
      initial.autoselectAmount,
      loaded.autoselect_amount,
      true,
    )
    config.autoselectPaymentMethod = findDefinedValue(
      initial.autoselectPaymentMethod,
      loaded.autoselect_payment_method,
      false,
    )

    config.purposeDetails = findDefinedValue(
      initial.purposeDetails,
      loaded.purpose_details,
      {},
    )
    config.amountSubunits = findDefinedValue(
      initial.amountSubunits,
      loaded.amount_subunits,
      {
        [PaymentCurrency.CHF]: 100,
        [PaymentCurrency.USD]: 100,
        [PaymentCurrency.EUR]: 100,
      },
    )
    config.allowCustomAmount = findDefinedValue(
      initial.allowCustomAmount,
      loaded.allow_custom_amount,
      true,
    )
    config.minimumCustomAmount = findDefinedValue(
      initial.minimumCustomAmount,
      loaded.minimum_custom_amount,
      1,
    )
    config.maximumCustomAmount = findDefinedValue(
      initial.maximumCustomAmount,
      loaded.maximum_custom_amount,
      undefined,
    )
    config.paymentProvider = findDefinedValue(
      initial.paymentProvider,
      loaded.payment_provider,
      PaymentProvider.DATATRANS,
    )
    config.allowedRecurringPaymentMethods = findDefinedValue(
      initial.allowedRecurringPaymentMethods,
      loaded.allowed_recurring_payment_methods,
      union(
        RecurringPaymentMethods,
        PseudoRecurringPaymentMethods,
      ) as PaymentMethod[],
    )
    config.paymentSlipDeliveryTypes = findDefinedValue(
      initial.paymentSlipDeliveryTypes,
      loaded.payment_slip_delivery_types,
      values(PaymentSlipDeliveryType) as PaymentSlipDeliveryType[],
    )
    config.defaultPaymentSlipDeliveryType = findDefinedValue(
      initial.defaultPaymentSlipDeliveryType,
      loaded.default_payment_slip_delivery_type,
      PaymentSlipDeliveryType.DOWNLOAD,
    )

    config.faqEntries = findDefinedValue(
      initial.faqEntries,
      loaded.faq_entries,
      [],
    )
    config.expandedFaqEntries = findDefinedValue(
      initial.expandedFaqEntries,
      loaded.expanded_faq_entries,
      [],
    )

    config.salutations = findDefinedValue(
      initial.salutations,
      loaded.salutations,
      ['mr', 'ms', 'none'],
    )
    config.countries = findDefinedValue(initial.countries, loaded.countries, [
      'CH',
      'DE',
      'FR',
      'IT',
    ])

    config.priorityCountries = findDefinedValue(
      initial.priorityCountries,
      loaded.priority_countries,
      ['CH', 'DE', 'FR', 'IT'],
    )

    config.states = findDefinedValue(initial.states, loaded.states, [])

    config.paymentComputations = findDefinedValue(
      initial.paymentComputations,
      loaded.payment_computations,
      [],
    )
    config.paymentValidations = findDefinedValue(
      initial.paymentValidations,
      loaded.payment_validations,
      {},
    )
    config.uiBreakpoints = findDefinedValue(
      initial.uiBreakpoints,
      loaded.ui_breakpoints,
      {
        'tamaro-bp-xs': 28,
        'tamaro-bp-sm': 33,
        'tamaro-bp-md': 48,
      },
    )
    config.uiTransitionTimeout = findDefinedValue(
      initial.uiTransitionTimeout,
      loaded.ui_transition_timeout,
      300,
    )
    config.uiCustomCheckableElements = findDefinedValue(
      initial.uiCustomCheckableElements,
      loaded.ui_custom_checkable_elements,
      true,
    )
    config.uiFloatingLabels = findDefinedValue(
      initial.uiFloatingLabels,
      loaded.ui_floating_labels,
      true,
    )
    config.uiRippleEffects = findDefinedValue(
      initial.uiRippleEffects,
      loaded.ui_ripple_effects,
      true,
    )
    config.uiCheckboxRadius = findDefinedValue(
      initial.uiCheckboxRadius,
      loaded.ui_checkbox_radius,
      0,
    )
    config.uiScrollOffset = findDefinedValue(
      initial.uiScrollOffset,
      loaded.ui_scroll_offset,
      20,
    )
    config.uiScrollDuration = findDefinedValue(
      initial.uiScrollDuration,
      loaded.ui_scroll_duration,
      300,
    )
    config.showStoredCustomerSalutation = findDefinedValue(
      initial.showStoredCustomerSalutation,
      loaded.show_stored_customer_salutation,
      true,
    )
    config.showStoredCustomerFirstname = findDefinedValue(
      initial.showStoredCustomerFirstname,
      loaded.show_stored_customer_firstname,
      true,
    )
    config.showStoredCustomerLastname = findDefinedValue(
      initial.showStoredCustomerLastname,
      loaded.show_stored_customer_lastname,
      true,
    )
    config.showStoredCustomerEmail = findDefinedValue(
      initial.showStoredCustomerEmail,
      loaded.show_stored_customer_email,
      true,
    )
    config.showStoredCustomerBirthdate = findDefinedValue(
      initial.showStoredCustomerBirthdate,
      loaded.show_stored_customer_birthdate,
      true,
    )
    config.showStoredCustomerEmailPermission = findDefinedValue(
      initial.showStoredCustomerEmailPermission,
      loaded.show_stored_customer_email_permission,
      true,
    )
    config.showStoredCustomerMessage = findDefinedValue(
      initial.showStoredCustomerMessage,
      loaded.show_stored_customer_message,
      true,
    )
    config.showStoredCustomerDonationReceipt = findDefinedValue(
      initial.showStoredCustomerDonationReceipt,
      loaded.show_stored_customer_donation_receipt,
      true,
    )
    config.showStoredCustomerStreet = findDefinedValue(
      initial.showStoredCustomerStreet,
      loaded.show_stored_customer_street,
      true,
    )
    config.showStoredCustomerStreetNumber = findDefinedValue(
      initial.showStoredCustomerStreetNumber,
      loaded.show_stored_customer_street_number,
      true,
    )
    config.showStoredCustomerStreet2 = findDefinedValue(
      initial.showStoredCustomerStreet2,
      loaded.show_stored_customer_street2,
      true,
    )
    config.showStoredCustomerPobox = findDefinedValue(
      initial.showStoredCustomerPobox,
      loaded.show_stored_customer_pobox,
      false,
    )
    config.showStoredCustomerZipCode = findDefinedValue(
      initial.showStoredCustomerZipCode,
      loaded.show_stored_customer_zip_code,
      true,
    )
    config.showStoredCustomerCity = findDefinedValue(
      initial.showStoredCustomerCity,
      loaded.show_stored_customer_city,
      true,
    )
    config.showStoredCustomerCountry = findDefinedValue(
      initial.showStoredCustomerCountry,
      loaded.show_stored_customer_country,
      true,
    )
    config.showStoredCustomerState = findDefinedValue(
      initial.showStoredCustomerState,
      loaded.show_stored_customer_state,
      true,
    )
    config.showBankbic = findDefinedValue(
      initial.showBankbic,
      loaded.show_bankbic,
      true,
    )
    config.showSubmitButton = findDefinedValue(
      initial.showSubmitButton,
      loaded.show_submit_button,
      true,
    )
    config.showFooter = findDefinedValue(
      initial.showFooter,
      loaded.show_footer,
      true,
    )
    config.showTestModeBar = findDefinedValue(
      initial.showTestModeBar,
      loaded.show_test_mode_bar,
      true,
    )
    config.showRetryPaymentButton = findDefinedValue(
      initial.showRetryPaymentButton,
      loaded.show_retry_payment_button,
      true,
    )
    config.ddFormTemplate = findDefinedValue(
      initial.ddFormTemplate,
      loaded.dd_form_template,
      null,
    )
    config.oneClickPayment = findDefinedValue(
      initial.oneClickPayment,
      loaded.one_click_payment,
      false,
    )
    config.dateStoreFormat = findDefinedValue(
      initial.dateStoreFormat,
      loaded.date_store_format,
      'YYYY-MM-DD',
    )
    config.dateDisplayFormat = findDefinedValue(
      initial.dateDisplayFormat,
      loaded.date_display_format,
      'DD / MM / YYYY',
    )

    config.customConfig = initial.customConfig

    config.recaptchaKey = findDefinedValue(
      initial.recaptchaKey,
      loaded.recaptcha_key,
      null,
    )

    config.slots = findDefinedValue(initial.slots, loaded.slots, {})
  })
}

export function createConfig(initial: WidgetConfig) {
  return observable(
    merge(createEmptyWidgetConfig(), initial, {
      language: findDefinedValue(initial.language, 'en'), // make sure language is set
    }),
  )
}

export function createComputedConfig(
  initial: WidgetConfig,
  translator: Translator,
): WidgetConfig {
  let computed = observable(createEmptyWidgetConfig())

  autorun(() => {
    let config = toJS(initial) // track all changes of the original config

    // load configurations inside yml files
    let loaded = translator.getConfigsWithFallbacks(config.language)

    if (config.translations) {
      translator.addTranslations(config.translations)
    }

    // compute config based on the initial one
    updateComputedConfig(
      computed,
      config,
      loaded,
      translator.getLoadedTranslations(),
    )
  })

  return computed
}
